import { Fragment } from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import { Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import classNames from 'classnames';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';

const useStyles = makeStyles({
  paragraphDescription: {
    color: grayColor[18],
  },
  paragraphFonts: {
    color: grayColor[18],
    fontSize: '14px',
    wordBreak: 'break-word',
    fontWeight: '400',
  },
  paragraphMarginReset: {
    marginBottom: '0',
  },
  divider: {
    margin: '20px 0',
  },
});

const ReportPreviewTitleRow = ({
  isFirstRow,
  firstName,
  lastName,
  username,
  multiplier,
  showMultiplier,
  selectedBrands,
  selectedSubBrands,
  selectedDrivers,
  selectedDeliveryPoint,
  recipeTags,
  recipeTagsMode,
  useSubBrands,
  useZoneCategories,
  individualMultipliersEnabled,
  selectedZoneCategories,
  includeShopElements,
  packedDate,
  shouldBeConsumedTo,
  selectedMealCategories = [],
  includeSubscriptions,
  groupClient,
  useVariants,
  selectedVariant,
  date,
  dateFrom,
  dateTo,
  dateType,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderRecipeTags = () => {
    const labels = recipeTags
      .filter(tag => tag.value !== '*')
      .map(tag => tag.label);

    if (labels.length === 0) {
      return '-';
    }

    if (!recipeTagsMode) {
      return labels.join(', ');
    }

    const operator = t(`reportPreview.recipeTagsOperator.${recipeTagsMode}`);

    return labels.map((label, index) => (
      <Fragment key={`${label}-${index}`}>
        {index > 0 && <strong>{` ${operator} `}</strong>}
        {label}
      </Fragment>
    ));
  };

  return (
    <div className={classes.wrapper}>
      {!isFirstRow && <Divider className={classes.divider} />}
      <GridContainer>
        {isFirstRow && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.generatedBy')}
            </p>
            <p
              className={classNames(
                classes.paragraphFonts,
                classes.paragraphMarginReset
              )}
            >
              {firstName} {lastName}
            </p>
            <p className={classes.paragraphFonts}>{username}</p>
          </GridItem>
        )}
        {selectedBrands?.length > 0 && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.brands')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedBrands
                .filter(brand => brand.value !== '*')
                .map(brand => brand.label)
                .join(', ') || '-'}
            </p>
          </GridItem>
        )}
        {useSubBrands && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.subbrands')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedSubBrands
                .filter(brand => brand && brand.value !== '*')
                .map(brand => brand.label)
                .join(', ') || '-'}
            </p>
          </GridItem>
        )}
        {useVariants && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.variant')}
            </p>
            <p className={classes.paragraphFonts}>{selectedVariant.name}</p>
          </GridItem>
        )}
        {useZoneCategories && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.zoneCategory')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedZoneCategories
                .filter(brand => brand.value !== '*')
                .map(zone => zone.label)
                .join(', ') || '-'}
            </p>
          </GridItem>
        )}
        {selectedDeliveryPoint !== undefined && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.deliveryType')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedDeliveryPoint
                ? t('reports.pickupPointDelivery')
                : t('reports.doorDelivery')}
            </p>
          </GridItem>
        )}
        {selectedDrivers !== undefined && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.drivers')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedDrivers
                .filter(driver => driver.value !== '*')
                .map(driver => driver.label)
                .join(', ') || '-'}
            </p>
          </GridItem>
        )}
        {showMultiplier && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.multiplier')}
            </p>
            <p className={classes.paragraphFonts}>
              {individualMultipliersEnabled
                ? t('reportPreview.isIndividualMultiplier')
                : multiplier}
            </p>
          </GridItem>
        )}
        {recipeTags && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.recipeTags')}
            </p>
            <p className={classes.paragraphFonts}>{renderRecipeTags()}</p>
          </GridItem>
        )}
        {selectedMealCategories.length > 0 && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.mealCategories')}
            </p>
            <p className={classes.paragraphFonts}>
              {selectedMealCategories
                .filter(category => category.value !== '*')
                .map(category => category.label)
                .join(', ') || '-'}
            </p>
          </GridItem>
        )}
        {dateType === 'single' ? (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.date')}
            </p>
            <p className={classes.paragraphFonts}>{date}</p>
          </GridItem>
        ) : (
          <>
            <GridItem xs={3}>
              <p className={classes.paragraphDescription}>
                {t('reportPreview.dateFrom')}
              </p>
              <p className={classes.paragraphFonts}>{dateFrom}</p>
            </GridItem>
            <GridItem xs={3}>
              <p className={classes.paragraphDescription}>
                {t('reportPreview.dateTo')}
              </p>
              <p className={classes.paragraphFonts}>{dateTo}</p>
            </GridItem>
          </>
        )}
        {(packedDate || packedDate === null) && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.packedDate')}
            </p>
            <p className={classes.paragraphFonts}>{packedDate || '-'}</p>
          </GridItem>
        )}
        {(shouldBeConsumedTo || shouldBeConsumedTo === null) && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.shouldBeConsumedTo')}
            </p>
            <p className={classes.paragraphFonts}>
              {shouldBeConsumedTo || '-'}
            </p>
          </GridItem>
        )}
        {includeSubscriptions !== undefined && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reportPreview.subscriptionIntent')}
            </p>
            <p className={classes.paragraphFonts}>
              {includeSubscriptions ? t('common.yes') : t('common.no')}
            </p>
          </GridItem>
        )}
        {includeShopElements !== undefined && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reports.includeShopElements')}
            </p>
            <p className={classes.paragraphFonts}>
              {includeShopElements ? t('common.yes') : t('common.no')}
            </p>
          </GridItem>
        )}
        {groupClient !== undefined && (
          <GridItem xs={3}>
            <p className={classes.paragraphDescription}>
              {t('reports.groupClient')}
            </p>
            <p className={classes.paragraphFonts}>
              {groupClient ? t('common.yes') : t('common.no')}
            </p>
          </GridItem>
        )}
      </GridContainer>
    </div>
  );
};

export default ReportPreviewTitleRow;
